/**
 * API client functions for the User Attribute Store (UAS) service
 * All endpoints proxy through /uas-api -> user-attribute-store:3005/api
 */

import { get, getPaginated, post, put, del } from '@/lib/api-client'
import type { PaginatedResponse } from '@/types/api'
import type {
  SubjectIdentity,
  UserAttributeValue,
  IdPConnection,
  ConnectionTestResult,
  SyncRun,
  UASAuditLog,
  CreateSubjectRequest,
  UpdateSubjectRequest,
  SetAttributeValueRequest,
  CreateIdPConnectionRequest,
  UpdateIdPConnectionRequest,
  SubjectQueryParams,
  AuditLogQueryParams,
} from '@/types/uas'
import type { BulkImportRequest, BulkImportResult } from '@/hooks/use-subject-upload'

function buildQueryString(
  params: Record<string, string | number | boolean | undefined | null>
): string {
  const qs = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null) {
      qs.set(key, String(value))
    }
  }
  const str = qs.toString()
  return str ? `?${str}` : ''
}

function encId(id: string): string {
  return encodeURIComponent(id)
}

// ============================================================================
// SUBJECTS
// ============================================================================

export function fetchSubjects(
  params: SubjectQueryParams = {}
): Promise<PaginatedResponse<SubjectIdentity>> {
  const qs = buildQueryString(params as Record<string, string | number | boolean | undefined | null>)
  return getPaginated<SubjectIdentity>(`/uas-api/subjects${qs}`)
}

export function fetchSubject(id: string): Promise<SubjectIdentity> {
  return get<SubjectIdentity>(`/uas-api/subjects/${encId(id)}`)
}

export function lookupSubject(
  identifier: string,
  identifierType: string
): Promise<SubjectIdentity> {
  const qs = buildQueryString({ identifier, type: identifierType })
  return get<SubjectIdentity>(`/uas-api/subjects/lookup${qs}`)
}

export function createSubject(data: CreateSubjectRequest): Promise<SubjectIdentity> {
  return post<SubjectIdentity, CreateSubjectRequest>('/uas-api/subjects', data)
}

export function updateSubject(
  id: string,
  data: UpdateSubjectRequest
): Promise<SubjectIdentity> {
  return put<SubjectIdentity, UpdateSubjectRequest>(`/uas-api/subjects/${encId(id)}`, data)
}

export function deleteSubject(id: string): Promise<void> {
  return del<void>(`/uas-api/subjects/${encId(id)}`)
}

// ============================================================================
// ATTRIBUTE VALUES
// ============================================================================

export function fetchSubjectAttributes(
  subjectId: string
): Promise<readonly UserAttributeValue[]> {
  return get<readonly UserAttributeValue[]>(
    `/uas-api/subjects/${encId(subjectId)}/attributes`
  )
}

export function fetchSubjectAttribute(
  subjectId: string,
  key: string
): Promise<UserAttributeValue> {
  return get<UserAttributeValue>(
    `/uas-api/subjects/${encId(subjectId)}/attributes/${encodeURIComponent(key)}`
  )
}

export function setSubjectAttribute(
  subjectId: string,
  key: string,
  data: SetAttributeValueRequest
): Promise<UserAttributeValue> {
  return post<UserAttributeValue, SetAttributeValueRequest>(
    `/uas-api/subjects/${encId(subjectId)}/attributes`,
    { ...data, attributeKey: key }
  )
}

export function deleteSubjectAttribute(
  subjectId: string,
  key: string
): Promise<void> {
  return del<void>(
    `/uas-api/subjects/${encId(subjectId)}/attributes/${encodeURIComponent(key)}`
  )
}

export function fetchAttributeHistory(
  subjectId: string,
  key: string
): Promise<readonly UserAttributeValue[]> {
  return get<readonly UserAttributeValue[]>(
    `/uas-api/subjects/${encId(subjectId)}/attributes/${encodeURIComponent(key)}/history`
  )
}

// ============================================================================
// BULK IMPORT
// ============================================================================

export function bulkImportAttributes(
  data: BulkImportRequest
): Promise<BulkImportResult> {
  return post<BulkImportResult, BulkImportRequest>(
    '/uas-api/attribute-values/bulk',
    data
  )
}

// ============================================================================
// IDP CONNECTIONS
// ============================================================================

export function fetchIdPConnections(): Promise<readonly IdPConnection[]> {
  return get<readonly IdPConnection[]>('/uas-api/idp-connections')
}

export function fetchIdPConnection(id: string): Promise<IdPConnection> {
  return get<IdPConnection>(`/uas-api/idp-connections/${encId(id)}`)
}

export function createIdPConnection(
  data: CreateIdPConnectionRequest
): Promise<IdPConnection> {
  return post<IdPConnection, CreateIdPConnectionRequest>(
    '/uas-api/idp-connections',
    data
  )
}

export function updateIdPConnection(
  id: string,
  data: UpdateIdPConnectionRequest
): Promise<IdPConnection> {
  return put<IdPConnection, UpdateIdPConnectionRequest>(
    `/uas-api/idp-connections/${encId(id)}`,
    data
  )
}

export function deleteIdPConnection(id: string): Promise<void> {
  return del<void>(`/uas-api/idp-connections/${encId(id)}`)
}

export function testIdPConnection(id: string): Promise<ConnectionTestResult> {
  return post<ConnectionTestResult>(`/uas-api/idp-connections/${encId(id)}/test`)
}

export function triggerIdPSync(id: string): Promise<{ syncRunId: string }> {
  return post<{ syncRunId: string }>(`/uas-api/idp-connections/${encId(id)}/sync`)
}

export function fetchIdPSyncHistory(
  id: string,
  params: { page?: number; pageSize?: number } = {}
): Promise<PaginatedResponse<SyncRun>> {
  const qs = buildQueryString(params as Record<string, string | number | boolean | undefined | null>)
  return getPaginated<SyncRun>(
    `/uas-api/idp-connections/${encId(id)}/sync-history${qs}`
  )
}

// ============================================================================
// SYNC RUNS
// ============================================================================

export function fetchSyncRuns(
  params: { page?: number; pageSize?: number } = {}
): Promise<PaginatedResponse<SyncRun>> {
  const qs = buildQueryString(params as Record<string, string | number | boolean | undefined | null>)
  return getPaginated<SyncRun>(`/uas-api/sync-runs${qs}`)
}

export function fetchSyncRun(id: string): Promise<SyncRun> {
  return get<SyncRun>(`/uas-api/sync-runs/${encId(id)}`)
}

export function cancelSyncRun(id: string): Promise<SyncRun> {
  return post<SyncRun>(`/uas-api/sync-runs/${encId(id)}/cancel`)
}

// ============================================================================
// AUDIT LOGS
// ============================================================================

export function fetchUASAuditLogs(
  params: AuditLogQueryParams = {}
): Promise<PaginatedResponse<UASAuditLog>> {
  const qs = buildQueryString(params as Record<string, string | number | boolean | undefined | null>)
  return getPaginated<UASAuditLog>(`/uas-api/audit-logs${qs}`)
}

export function fetchSubjectAuditLogs(
  subjectId: string
): Promise<readonly UASAuditLog[]> {
  return get<readonly UASAuditLog[]>(
    `/uas-api/audit-logs/subject/${encId(subjectId)}`
  )
}
